// Brand palette catalogue.
//
// Each palette is a flat map of CSS custom properties applied to
// document.documentElement. The base set (defined in index.css) is
// "Aurora" — the cool purple/pink/blue iridescent identity we've been
// shipping. The alternates below are candidates the founder is
// auditioning before locking one in.
//
// Adding a palette: pick five gradient-bg stops (saturated, dark-
// friendly), four gradient-text stops (slightly lighter / lifted so
// they read on dark glass), four aurora radial-blob colours with
// pre-baked alpha, a deep background fall-through, and a single
// "glow" colour for box-shadow accents. Everything else flows from
// those tokens.

export type PaletteId = 'aurora' | 'daybreak' | 'sunset' | 'forest' | 'mono' | 'ember'

export interface Palette {
  id: PaletteId
  name: string
  description: string
  vars: Record<string, string>
  // Light-theme overrides. Applied on top of `vars` when
  // <html data-theme="light">. Typically only the aurora colours
  // need to change — pure-white pages can't take the saturated
  // dark-friendly washes from `vars`. Keys not present here fall
  // through to whatever the [data-theme="light"] CSS rules define.
  lightVars?: Record<string, string>
}

export const PALETTES: Palette[] = [
  {
    id: 'aurora',
    name: 'Aurora',
    description: 'Pink → violet → blue iridescent (current default).',
    vars: {
      '--grad-text-1': '#ff9ad5',
      '--grad-text-2': '#cf94ff',
      '--grad-text-3': '#a6b4ff',
      '--grad-text-4': '#8ad4ff',
      '--grad-bg-1':   '#ff6ad5',
      '--grad-bg-2':   '#c777b9',
      '--grad-bg-3':   '#ad8cff',
      '--grad-bg-4':   '#8aaeff',
      '--grad-bg-5':   '#76e7ff',
      '--aurora-1':    'rgba(124, 109, 255, 0.42)',
      '--aurora-2':    'rgba(255,  90, 200, 0.30)',
      '--aurora-3':    'rgba(255, 150,  88, 0.22)',
      '--aurora-4':    'rgba( 88, 220, 255, 0.26)',
      '--bg-deep':     '#06060c',
      '--glow-accent': 'rgba(173, 140, 255, 0.6)',
    },
    lightVars: {
      '--aurora-1':    'rgba(180, 165, 255, 0.45)',   // soft violet top-left
      '--aurora-2':    'rgba(255, 175, 230, 0.40)',   // soft pink top-right
    },
  },
  {
    id: 'daybreak',
    name: 'Daybreak',
    description: 'Peach → lilac. Soft pastels — the current default for light theme.',
    vars: {
      // For dark theme, give Daybreak warm-dawn colours so the
      // palette is still differentiated from Aurora when toggled.
      '--grad-text-1': '#ffd4b0',
      '--grad-text-2': '#ffb0c8',
      '--grad-text-3': '#d8b0ff',
      '--grad-text-4': '#a8c8ff',
      '--grad-bg-1':   '#ffb27a',
      '--grad-bg-2':   '#ff8aa6',
      '--grad-bg-3':   '#d090ff',
      '--grad-bg-4':   '#90b6ff',
      '--grad-bg-5':   '#9bd4ff',
      '--aurora-1':    'rgba(255, 180, 140, 0.36)',
      '--aurora-2':    'rgba(220, 160, 255, 0.30)',
      '--aurora-3':    'rgba(255, 200, 165, 0.20)',
      '--aurora-4':    'rgba(180, 170, 255, 0.22)',
      '--bg-deep':     '#0c080d',
      '--glow-accent': 'rgba(255, 175, 145, 0.6)',
    },
    lightVars: {
      // The reference pair: peach top-left, lilac top-right.
      '--aurora-1':    'rgba(255, 198, 162, 0.55)',
      '--aurora-2':    'rgba(214, 174, 255, 0.50)',
    },
  },
  {
    id: 'sunset',
    name: 'Sunset',
    description: 'Coral → amber → peach → lilac.',
    vars: {
      '--grad-text-1': '#ffb38a',
      '--grad-text-2': '#ff8e9c',
      '--grad-text-3': '#ff7ebf',
      '--grad-text-4': '#c89eff',
      '--grad-bg-1':   '#ffb27a',
      '--grad-bg-2':   '#ff7a8b',
      '--grad-bg-3':   '#f0639f',
      '--grad-bg-4':   '#bb6cf3',
      '--grad-bg-5':   '#7c8aff',
      '--aurora-1':    'rgba(255, 138, 100, 0.42)',
      '--aurora-2':    'rgba(255, 100, 150, 0.32)',
      '--aurora-3':    'rgba(255, 200, 120, 0.24)',
      '--aurora-4':    'rgba(190, 120, 255, 0.24)',
      '--bg-deep':     '#0c070c',
      '--glow-accent': 'rgba(255, 145, 130, 0.6)',
    },
    lightVars: {
      '--aurora-1':    'rgba(255, 178, 142, 0.55)',   // warm coral wash
      '--aurora-2':    'rgba(255, 168, 200, 0.45)',   // pink wash
    },
  },
  {
    id: 'forest',
    name: 'Forest',
    description: 'Emerald → teal → sage → glacier blue.',
    vars: {
      '--grad-text-1': '#a8f5b8',
      '--grad-text-2': '#7eead4',
      '--grad-text-3': '#7fbef3',
      '--grad-text-4': '#a9d6ff',
      '--grad-bg-1':   '#34e1a0',
      '--grad-bg-2':   '#2bd0c4',
      '--grad-bg-3':   '#4fb0e8',
      '--grad-bg-4':   '#6f8af0',
      '--grad-bg-5':   '#a59cff',
      '--aurora-1':    'rgba(60, 200, 140, 0.36)',
      '--aurora-2':    'rgba(80, 200, 220, 0.28)',
      '--aurora-3':    'rgba(120, 180, 255, 0.24)',
      '--aurora-4':    'rgba(200, 240, 200, 0.18)',
      '--bg-deep':     '#050c0a',
      '--glow-accent': 'rgba(80, 220, 180, 0.6)',
    },
    lightVars: {
      '--aurora-1':    'rgba(160, 230, 200, 0.45)',   // sage / mint wash
      '--aurora-2':    'rgba(170, 210, 240, 0.45)',   // glacier blue wash
    },
  },
  {
    id: 'mono',
    name: 'Monochrome',
    description: 'Silver → pearl → graphite. Quiet, editorial.',
    vars: {
      '--grad-text-1': '#f4f6fb',
      '--grad-text-2': '#cfd4e3',
      '--grad-text-3': '#aab1c4',
      '--grad-text-4': '#dfe3ee',
      '--grad-bg-1':   '#e6e9f2',
      '--grad-bg-2':   '#b8bdd0',
      '--grad-bg-3':   '#8c92a8',
      '--grad-bg-4':   '#a8aec2',
      '--grad-bg-5':   '#dfe3ee',
      '--aurora-1':    'rgba(200, 205, 220, 0.18)',
      '--aurora-2':    'rgba(160, 168, 190, 0.14)',
      '--aurora-3':    'rgba(220, 226, 240, 0.10)',
      '--aurora-4':    'rgba(140, 150, 175, 0.12)',
      '--bg-deep':     '#07080c',
      '--glow-accent': 'rgba(200, 210, 230, 0.55)',
    },
    lightVars: {
      // Mono is editorial / minimal — barely-there silver washes.
      '--aurora-1':    'rgba(200, 205, 220, 0.30)',
      '--aurora-2':    'rgba(220, 226, 240, 0.25)',
    },
  },
  {
    id: 'ember',
    name: 'Ember',
    description: 'Gold → orange → crimson → maroon.',
    vars: {
      '--grad-text-1': '#ffd896',
      '--grad-text-2': '#ffb073',
      '--grad-text-3': '#ff8a73',
      '--grad-text-4': '#f17a8a',
      '--grad-bg-1':   '#ffcf6a',
      '--grad-bg-2':   '#ff9e5b',
      '--grad-bg-3':   '#f76c5e',
      '--grad-bg-4':   '#d54a72',
      '--grad-bg-5':   '#9b3a82',
      '--aurora-1':    'rgba(255, 170, 90, 0.36)',
      '--aurora-2':    'rgba(255, 100, 80, 0.30)',
      '--aurora-3':    'rgba(180, 60, 110, 0.24)',
      '--aurora-4':    'rgba(255, 210, 150, 0.18)',
      '--bg-deep':     '#0d0608',
      '--glow-accent': 'rgba(255, 160, 110, 0.6)',
    },
    lightVars: {
      '--aurora-1':    'rgba(255, 195, 130, 0.55)',   // gold wash
      '--aurora-2':    'rgba(255, 165, 165, 0.45)',   // ember pink wash
    },
  },
]

const PALETTE_STORAGE_KEY = 'massing_palette'

// Vars that paint the page background / aurora. In light mode the
// dark-tuned values from `vars` MUST NOT win — that's the bug that
// kept the light theme rendering as dark grey. A palette can still
// affect the light bg by setting an entry in `lightVars` (typically
// pastel aurora-1 / aurora-2 colours).
const LIGHT_THEME_RESERVED_VARS = new Set([
  '--bg-deep',
  '--aurora-1',
  '--aurora-2',
  '--aurora-3',
  '--aurora-4',
  '--glow-accent',
])

// Every CSS variable any palette can possibly set, so we know what
// to clear before applying a new palette (otherwise tokens from a
// previous palette can linger on <html>'s inline style).
function _allPaletteKeys(): Set<string> {
  const out = new Set<string>()
  PALETTES.forEach((p) => {
    Object.keys(p.vars).forEach((k) => out.add(k))
    if (p.lightVars) Object.keys(p.lightVars).forEach((k) => out.add(k))
  })
  return out
}

export function applyPalette(id: PaletteId): void {
  const p = PALETTES.find((x) => x.id === id) ?? PALETTES[0]
  const root = document.documentElement
  const isLight = root.getAttribute('data-theme') === 'light'

  // Clear everything any palette could have set so leftovers from a
  // prior palette don't bleed into the new one.
  _allPaletteKeys().forEach((k) => root.style.removeProperty(k))

  // Apply dark vars, but in light mode skip the reserved page-bg /
  // aurora variables — those are only allowed in via lightVars.
  Object.entries(p.vars).forEach(([k, v]) => {
    if (isLight && LIGHT_THEME_RESERVED_VARS.has(k)) return
    root.style.setProperty(k, v)
  })

  // In light mode, layer the palette's lightVars on top. Typically
  // just aurora-1 / aurora-2 — the page-bg gradient that the user
  // can see actually change as they switch palettes.
  if (isLight && p.lightVars) {
    Object.entries(p.lightVars).forEach(([k, v]) => {
      root.style.setProperty(k, v)
    })
  }

  try {
    localStorage.setItem(PALETTE_STORAGE_KEY, p.id)
  } catch {
    /* private mode / quota — non-fatal */
  }
}

/** Strip every palette-controlled CSS variable from the inline style
 * on <html>. After clearing, whatever the matching [data-theme=...]
 * rules in index.css define is what the page wears. */
export function clearPalette(): void {
  const root = document.documentElement
  _allPaletteKeys().forEach((k) => root.style.removeProperty(k))
}

export function loadPaletteId(): PaletteId {
  try {
    const raw = localStorage.getItem(PALETTE_STORAGE_KEY) as PaletteId | null
    if (raw && PALETTES.some((p) => p.id === raw)) return raw
  } catch {
    /* storage unavailable */
  }
  return 'aurora'
}
